import * as React from 'react';
import { Props } from './InsuranceUpsale';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, useTheme } from '../../../theme';
import { AggregatorInsuranceGroups } from '@entities/insurance';
import { useInsuranceGroupTexts, useInsuranceUpsale } from './hooks';
import { CartServiceInsurance, OnDeleteItemType } from '../../../Cart/types';
import { Money } from '../../../Money';
import { useRenderers } from '../../../renderProps';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import InsuranceSummary from '../../../ServiceSummary/services/InsuranceSummary';
import cn from 'classnames';
import { AdditionalServiceType, ServiceButton } from '../../types';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import { InsurancePageModal, InsuranceUpsaleModal } from '../../../InsurancePage';
import SimpleLoader from '../../../SimpleLoader';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { ModalX } from '../../../BaseComponents';

const InsuranceUpsaleAggregator: React.FC<Props & { hideCard?: boolean }> = ({
	insurancePrograms,
	groupName,
	segmentId,
	hideCard
}) => {
	const { t } = useTranslation('Checkout');
	const { InsuranceUpsale: theme, Insurance: cardStyles } = useTheme('CheckoutAdditionalService');

	const model = useInsuranceUpsale(insurancePrograms);
	const groupTexts = useInsuranceGroupTexts(groupName, insurancePrograms);

	const onServiceDelete: OnDeleteItemType = async (_, service) => {
		const insuranceService = service as CartServiceInsurance;

		await model.onDeleteInsurance(insuranceService.code);
	};

	const renderPriceDescription = (isSelected: boolean) => {
		return isSelected ? (
			model.totalPrice.amount > 0 && (
				<div className={theme.price}>
					<span className={theme.price__label}>{t('Total')}</span>
					<Money moneyClassName={theme.money} money={model.totalPrice} />
					<CdnIcon icon="Info/Item Icluded" className={theme.includedIcon} />
				</div>
			)
		) : (
			<>
				<span className={theme.forAll}>{t('For all passengers')}</span>
				<div className={theme.price}>
					{model.isDifferentPrices && <span className={theme.price__label}>{t('from')}</span>}
					<Money moneyClassName={theme.money} money={model.minPrice} />
				</div>
			</>
		);
	};

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	const onAddClick = model.isSingleToggleMode ? model.onAddSingle : () => model.open();

	const buttons: ServiceButton[] = React.useMemo(() => {
		if (model.isReadonly) {
			return [
				{
					text: t('More details'),
					action: model.open,
					className: cn({
						[theme.loading]: model.isLoading
					})
				}
			];
		}

		return [
			{
				text: model.isSingleToggleMode ? t('Remove protection') : t('Change'),
				action: model.isSingleToggleMode ? model.onDeleteSingle : model.open,
				className: cn({
					[theme.loading]: model.isLoading
				})
			}
		];
	}, [
		model.isReadonly,
		model.isLoading,
		model.isSingleToggleMode,
		model.open,
		model.onDeleteSingle,
		t,
		theme.loading
	]);

	return (
		<>
			<InsuranceSummary
				onServiceDelete={
					model.isReadonly || model.totalPrice.amount === 0 ? null : onServiceDelete
				}
				selectedInsurances={model.selectedPrograms}
				isConfirmed={model.isConfirmed}
				isDownload
				onDownloadVskInsurance={model.onDownloadVskInsurance}
				mode="aggregator"
			>
				{items => {
					const isSelected = items.length > 0;

					return (
						<ThemeProvider
							value={{
								ServiceSummary: {
									ServiceSummaryOption: {
										option: cardStyles.selectedOption,
										option__name: cardStyles.option__name
									}
								}
							}}
						>
							<ServiceWrapper
								header={groupTexts.title}
								renderHeaderPrice={() => renderPriceDescription(isSelected)}
								serviceType={AdditionalServiceType.Insurance}
								className={cn(cardStyles.insurance, {
									[cardStyles.insurance_hidden]: hideCard
								})}
								coverClassName={cardStyles.insurance_aggregator}
								isLoading={model.isLoading}
								isSelected={isSelected}
								priceFrom={model.minPrice}
								priceTotal={model.totalPrice}
								hideFooterButtons={!model.canModify}
								aggregationIcons={[
									<CdnIcon icon="Services/Insurance" />,
									<CdnIcon icon="Payment/Cash" />
								]}
								mode="aggregator"
								description={
									<div>
										<SafeHtml tag="div" html={groupTexts.descr} />
										<ul>
											{groupTexts.options.map(option => (
												<SafeHtml key={option} tag="li" html={option} />
											))}
										</ul>
									</div>
								}
								addClick={onAddClick}
								buttons={buttons}
							>
								<ServiceSummaryDivider>
									<AggregatorInsuranceGroups
										items={items}
										programs={insurancePrograms}
										segmentId={segmentId}
									/>
								</ServiceSummaryDivider>
							</ServiceWrapper>
						</ThemeProvider>
					);
				}}
			</InsuranceSummary>

			<ModalX
				classes={{ scrollBody: theme.scrollBody, paper: theme.paper }}
				isOpen={model.isOpen}
				size="fullscreen"
			>
				<InsurancePageModal
					availablePrograms={model.availablePrograms}
					onServicesChanged={model.setIsNeedToSendSaveReq}
					onTotalInsurancePriceChange={model.setInsurancePriceToPay}
					onSaveOrderServicesParamsChange={model.setSaveOrderServicesParams}
					onDownloadNasInsurance={model.onDownloadVskInsurance}
					totalPrice={model.insurancePriceToPay}
					onConfirm={model.onConfirm}
					onClose={model.onClose}
				/>
				{model.isLoading && <SimpleLoader />}
			</ModalX>

			<InsuranceUpsaleModal
				insurance={model.priorityInsurance}
				isOpen={model.isUpsaleOpen}
				onAgree={model.handleAgree}
				onDisagree={model.handleDisagree}
			/>
		</>
	);
};

export default InsuranceUpsaleAggregator;
